export const ADVANCED_RECORDS_HELP_SECTIONS = [
  {
    id: "when-to-use",
    title: "When to use Advanced setup and records",
    description:
      "Use these pages when the normal rental setup flow does not answer a deeper control, agreement, or support question.",
    bullets: [
      "Most owners should start with Continue Setup or Edit Setup.",
      "Open these pages when you need setup checks, client and payment control, agreement versions, setup history, or support records.",
      "These pages are for reviewing and recording decisions, not for everyday listing edits.",
    ],
  },
  {
    id: "setup-checks",
    title: "Setup Checks",
    description:
      "Review renter-rule settings that may affect publishing, agreement behavior, and renter expectations.",
    bullets: [
      "Use this when the rental arrangement needs a deeper check before publishing.",
      "Save changes after confirming the operating relationship, rent model, client ownership, schedule control, and product rules.",
      "If a warning appears, review the related field before asking a renter to accept an agreement.",
    ],
  },
  {
    id: "client-payment-control",
    title: "Client & Payment Control",
    description:
      "Define who controls client records, payments, and agreement acknowledgement for the workspace.",
    bullets: [
      "Use this when the salon and renter need clear expectations about client ownership.",
      "Payment collector controls how the system interprets payment and ledger responsibility.",
      "Agreement acknowledgement should stay on when renters must accept terms before activation.",
    ],
  },
  {
    id: "agreement-setup",
    title: "Agreement Setup",
    description:
      "Manage agreement text, versions, active agreement status, and renter acceptance records.",
    bullets: [
      "Use version numbers when agreement text changes so acceptance history stays clear.",
      "Use the summary for a plain-language overview and the terms field for the written agreement text.",
      "Record acceptance only after the renter has reviewed and accepted the current agreement version.",
    ],
  },
  {
    id: "setup-history",
    title: "Setup History",
    description:
      "Review dated setup changes and mark a setup check version reviewed after the owner has checked it.",
    bullets: [
      "Use this page as a record of owner review and setup changes.",
      "Add a short review note that explains what was checked.",
      "Setup history helps the salon understand what changed and when without showing technical audit code.",
    ],
  },
  {
    id: "support-follow-up",
    title: "Support & Follow-up",
    description:
      "Track service events, warranty follow-up, follow-up cases, and renter support records for an active rental workspace.",
    bullets: [
      "Use this after a renter is active or when a rental support issue needs a record.",
      "Service events capture what happened; follow-up cases capture what still needs attention.",
      "Only add support records that the salon team needs to track operationally.",
    ],
  },
  {
    id: "rental-request-preview",
    title: "Rental Request Preview",
    description:
      "Preview what a renter would see when checking availability or starting a rental request.",
    bullets: [
      "Use this as a renter-facing workflow check.",
      "If the preview does not look right, return to the main listing setup before publishing or sharing the listing.",
    ],
  },
];

export const ADVANCED_RECORD_TOOLTIPS = {
  "Advanced setup and records":
    "Opens deeper setup, agreement, activity, and support records for this workspace.",
  "Setup Checks":
    "Review renter-rule settings and conflicts that can affect publishing or agreement acceptance.",
  "Client & Payment Control":
    "Set who controls client records, payments, and agreement acknowledgement.",
  "Agreement Setup":
    "Manage agreement text, versions, active agreement status, and acceptance records.",
  "Setup History":
    "Review dated setup changes and record that the owner checked the current setup version.",
  "Rental Request Preview":
    "Open the renter-facing request flow to preview availability and checkout behavior.",
  "Support & Follow-up":
    "Track service events, warranty follow-up, and support cases for an active rental workspace.",
  Listing:
    "Return to the main workspace setup page for normal listing edits.",
  "Warning level":
    "Shows whether setup checks found no issue, a review item, or a blocker.",
  "Approval mode":
    "Controls how much review is needed before a renter can move forward.",
  "Rent model":
    "Shows how rent is structured for the workspace.",
  "Client ownership":
    "Shows whether the salon or renter owns the client relationship.",
  "Operating relationship":
    "Shows the rental relationship Ellyanna Boothshare uses for launch. New workspace rentals are for signed-in independent professionals.",
  "Profile name":
    "A private label for this setup profile, used to identify the rule set later.",
  "Who owns clients":
    "Choose who should control the client relationship and related records.",
  "Who sets service prices":
    "Choose whether the salon or renter controls service pricing for this workspace.",
  "Who controls schedule":
    "Choose who controls the day-to-day work schedule, separate from listing availability.",
  "Who provides products":
    "Choose whether products are supplied by the renter, salon, or both.",
  Notes:
    "Use notes for operational context, internal review details, or exceptions.",
  "Can the renter work elsewhere?":
    "Use this to show whether the renter can operate independently outside this salon.",
  "Can the renter use their own business branding?":
    "Use this to show whether the renter can present their own independent business name.",
  "Are payments collected directly by the renter?":
    "Use this to show whether the renter or salon/platform controls client-facing collection.",
  "Can the renter use independent products?":
    "Use this to show whether the renter can use their own products or supplies.",
  "Can the salon require desk shifts?":
    "Use this to flag required non-rental duties that may conflict with independent rental setup.",
  "Can the salon require fixed hours?":
    "Use this to flag fixed schedule requirements that may affect independent rental setup.",
  "Client ownership mode":
    "Choose who owns and controls client records for this rented workspace.",
  "Payment collector":
    "Choose who is treated as collecting rental or client payments.",
  "Require agreement acknowledgement before activation?":
    "Keep this on when renters must accept terms before the workspace is active.",
  Title:
    "The agreement name the salon team will recognize.",
  Version:
    "Use a clear version such as 1.0 or 2026-04 so changes can be tracked.",
  "Agreement type":
    "The type of written terms attached to this workspace.",
  "When is it required?":
    "Choose when the renter must review or accept the agreement.",
  Summary:
    "A plain-language overview of what the agreement covers.",
  "Agreement terms":
    "The written terms the renter should review and accept.",
  "Use this agreement for this workspace?":
    "Turn this on to make this agreement the active one for the workspace.",
  "Signature name":
    "The name recorded for the renter acceptance.",
  Note:
    "Optional internal note about the acceptance record.",
  "Renter accepted the role, ownership, and payment terms?":
    "Record this only after the renter has accepted the current terms.",
  "Review note":
    "Add a short owner-facing note about what was reviewed.",
  "Client ID":
    "Optional client record connected to this service event.",
  "Renter user ID":
    "The renter or independent professional connected to this support record.",
  "Service started":
    "When the service or support event started.",
  "Service ended":
    "When the service or support event ended, if known.",
  "Service category":
    "A short category such as warranty, complaint, repair, or follow-up.",
  "Service name":
    "The service or support item being recorded.",
  Amount:
    "Optional amount connected to the support event.",
  "Follow-up model":
    "Choose who is responsible for follow-up.",
  "Warranty expires":
    "Date the warranty or follow-up window ends, if applicable.",
  "Backup professional user ID":
    "Optional backup professional responsible for follow-up.",
  "Renter contact route":
    "How the renter or independent professional should be contacted.",
  "Support notes":
    "Operational notes about the support event.",
  "Service event":
    "Choose the recorded service event this follow-up case belongs to.",
  Subject:
    "Short title for the follow-up case.",
  "Assigned backup professional user ID":
    "Optional backup professional assigned to help with the case.",
  Description:
    "Describe what needs to be followed up.",
};

export function getAdvancedRecordTooltip(label) {
  return ADVANCED_RECORD_TOOLTIPS[label] || "";
}
